import React, { useContext, useMemo } from "react";
import { DashboardContext } from "../contexts/DashboardContext";
import StatCard from "./StatCard";

export default function StatsContainer() {
  // Pull live data directly from Context instead of static JSON
  const { employeesData, totalEmployees } = useContext(DashboardContext);

  // Dynamically calculate the totals based entirely on backend data
  const dynamicStats = useMemo(() => {
    const data = employeesData || [];

    // 1. Total Employees
    const total = totalEmployees || data.length;

    // 2. Active Employees
    const active = data.filter(
      (emp) => (emp.status || "").toLowerCase() === "active",
    ).length;

    // 3. New Joiners (Employees created in the last 30 days)
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const newJoiners = data.filter((emp) => {
      if (!emp.created_at) return false;
      return new Date(emp.created_at) >= thirtyDaysAgo;
    }).length;

    // 4. On Leave Today
    const onLeave = data.filter(
      (emp) => (emp.status || "").toLowerCase() === "on leave",
    ).length;

    // 5. Pending Documents
    // Checks if the backend sends document arrays with the employee, otherwise defaults to 0 safely
    const pendingDocs = data.reduce((count, emp) => {
      if (!emp.documents) return count;
      const pending = emp.documents.filter(
        (doc) =>
          (doc.verification_status || doc.status || "").toLowerCase() ===
          "pending",
      ).length;
      return count + pending;
    }, 0);

    return [
      {
        id: 1,
        title: "Total Employees",
        value: total,
        icon: "Users",
        iconBg: "bg-blue-50",
        iconColor: "text-blue-600",
      },
      {
        id: 2,
        title: "Active Employees",
        value: active,
        icon: "UserCheck",
        iconBg: "bg-emerald-50",
        iconColor: "text-emerald-600",
      },
      {
        id: 3,
        title: "New Joiners",
        value: newJoiners,
        icon: "UserPlus",
        iconBg: "bg-purple-50",
        iconColor: "text-purple-600",
      },
      {
        id: 4,
        title: "On Leave Today",
        value: onLeave,
        icon: "CalendarOff",
        iconBg: "bg-amber-50",
        iconColor: "text-amber-600",
      },
      {
        id: 5,
        title: "Pending Documents",
        value: pendingDocs,
        icon: "FileText",
        iconBg: "bg-red-50",
        iconColor: "text-red-600",
      },
    ];
  }, [employeesData, totalEmployees]);

  return (
    // Responsive Grid: 2 cols on mobile, 3 on tablet, 5 on desktop
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3 md:gap-4 w-full">
      {dynamicStats.map((stat) => (
        <StatCard
          key={stat.id}
          title={stat.title}
          value={stat.value.toLocaleString()}
          iconName={stat.icon}
          iconBg={stat.iconBg}
          iconColor={stat.iconColor}
        />
      ))}
    </div>
  );
}
